import * as Sentry from '@sentry/vue';
import { KycSessionStorage } from '@/common/KycSessionStorage';
import type {
  KycSessionDtoFlowStepName,
  KycSessionDtoFlowType,
} from '@/integrations/Immersve/AmethystClient';
import type { KycSessionData } from '@/stores/auth.store';
import type { KycSession } from './KycSession';
import { KycSessionFlow } from './KycSessionFlow';
import { StepSet, type StepName } from './UserFlow';

const AMETHYST_STEP_NAME_BY_FLUORITE_STEP: Record<StepName, KycSessionDtoFlowStepName | undefined> =
  {
    Region: 'region',
    Consent: 'consent',
    Contact: 'contact-channels',
    Spending: 'expected-spend',
    Address: 'residential-address',
    ProofOfAddress: 'proof-of-address',
    'ID Verification': 'identity-verification',
    Confirmation: undefined,
  };

const AMETHYST_FLOW_TYPE_BY_STEP_SET: Record<StepSet, KycSessionDtoFlowType> = {
  [StepSet.FULL]: 'kyc-identity-verification',
  [StepSet.CONTACTDETAILS]: 'kyc-identity-review',
  [StepSet.CONTACTS_WITH_PENDING_KYC]: 'kyc-identity-review',
};

interface KycFlowComparisonProps {
  kycSessionId: string;
  fluoriteFlow: KycSessionFlow;
  amethystFlow: KycSessionFlow;
  isRegionSelected: boolean;
  hasSavedStepSet: boolean;
}

export class KycFlowComparison {
  readonly kycSessionId: string;
  readonly fluoriteFlow: KycSessionFlow;
  readonly amethystFlow: KycSessionFlow;
  readonly isRegionSelected: boolean;
  readonly hasSavedStepSet: boolean;

  private constructor(props: KycFlowComparisonProps) {
    this.kycSessionId = props.kycSessionId;
    this.fluoriteFlow = props.fluoriteFlow;
    this.amethystFlow = props.amethystFlow;
    this.isRegionSelected = props.isRegionSelected;
    this.hasSavedStepSet = props.hasSavedStepSet;
  }

  static forKycSession(
    kycSession: KycSession,
    previousComparison?: KycFlowComparison
  ): KycFlowComparison | undefined {
    if (!kycSession.flow || kycSession.isCompleted() || kycSession.isCancelled()) {
      return undefined;
    }

    const savedSessionData = KycSessionStorage.getDataForSession<KycSessionData>(kycSession.id);

    return new KycFlowComparison({
      kycSessionId: kycSession.id,
      fluoriteFlow: KycFlowComparison.fluoriteFlowFor(kycSession),
      amethystFlow: kycSession.flow,
      isRegionSelected: Boolean(kycSession.region),
      hasSavedStepSet:
        previousComparison?.hasSavedStepSet ?? savedSessionData?.initialStepSet !== undefined,
    });
  }

  isMatch(): boolean {
    return this.fluoriteFlow.equals(this.amethystFlow);
  }

  isOutdatedBy(kycSession: KycSession): boolean {
    return !this.isRegionSelected && Boolean(kycSession.region);
  }

  isSameMismatchAs(other: KycFlowComparison | undefined): boolean {
    return (
      other !== undefined &&
      !this.isMatch() &&
      !other.isMatch() &&
      this.mismatchKey() === other.mismatchKey()
    );
  }

  reportMismatch() {
    if (this.isMatch()) {
      return;
    }

    Sentry.captureMessage('KYC flow mismatch', {
      level: 'warning',
      fingerprint: ['kyc-flow-mismatch', this.fluoriteFlow.type, this.amethystFlow.type],
      tags: {
        kycSessionId: this.kycSessionId,
        fluoriteFlowType: this.fluoriteFlow.type,
        amethystFlowType: this.amethystFlow.type,
      },
      extra: {
        fluoriteSteps: this.fluoriteFlow.steps,
        amethystSteps: this.amethystFlow.steps,
        isRegionSelected: this.isRegionSelected,
        hasSavedStepSet: this.hasSavedStepSet,
      },
    });
  }

  private static fluoriteFlowFor(kycSession: KycSession): KycSessionFlow {
    return new KycSessionFlow({
      type: AMETHYST_FLOW_TYPE_BY_STEP_SET[kycSession.steps.initialStepSet],
      steps: kycSession.steps.steps
        .filter((step) => step.name !== 'ProofOfAddress' || kycSession.isPoaRequired())
        .flatMap((step) => AMETHYST_STEP_NAME_BY_FLUORITE_STEP[step.name] ?? []),
    });
  }

  private mismatchKey(): string {
    const fluoriteSteps = this.fluoriteFlow.steps;
    const amethystSteps = this.amethystFlow.steps;
    const sharedFluoriteSteps = fluoriteSteps.filter((step) => amethystSteps.includes(step));
    const sharedAmethystSteps = amethystSteps.filter((step) => fluoriteSteps.includes(step));

    return JSON.stringify({
      fluoriteType: this.fluoriteFlow.type,
      amethystType: this.amethystFlow.type,
      onlyInFluorite: fluoriteSteps.filter((step) => !amethystSteps.includes(step)),
      onlyInAmethyst: amethystSteps.filter((step) => !fluoriteSteps.includes(step)),
      isSameOrder: sharedFluoriteSteps.join() === sharedAmethystSteps.join(),
    });
  }
}
